import { useEffect, useState } from "react";
import Status from "../../../enums/Status";
import IVacancy from "../../../models/recruitment/IVacancy";
import recruitmentService from "../../../services/RecruitmentService";
import { useTranslation } from "react-i18next";
import { makeStyles, Text, Title3 } from "@fluentui/react-components";
import { dolphinTokens } from "../../../utils/Theme";

const useStyles = makeStyles({
    section: {
        maxWidth: "900px",
        margin: "0 auto",
        padding: "32px 16px",
        marginLeft: "auto",
        marginRight: "auto",
    },
    card: {
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        padding: "24px",
        marginBottom: "0",
        maxWidth: "700",
        width: "100%",
        textAlign: "left"
    },
    cardTitle: {
        marginBottom: "8",
        color: dolphinTokens.colorLightBlue,
    },
});

export default function Vacancies() {
    const { t } = useTranslation("recruitment");
    const styles = useStyles();

    const [vacancies, setVacancies] = useState<IVacancy[]>([]);
    const [status, setStatus] = useState<Status>(Status.LOADING);
    
    useEffect(() => {
        getVacancies(setVacancies, setStatus);
    }, []);

    return (
        <section
            className={styles.section}
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
                minWidth: 300,
                maxWidth: 700,
            }}
        >
            <Title3 as="h2" block className={styles.cardTitle}>
                {t("vacancies.title")}
            </Title3>

            <div className={styles.card}>
                {
                    (status === Status.LOADING && <Text>{t("vacancies.loading")}</Text>) ||
                    (status === Status.LOAD_ERROR && <Text>{t("vacancies.error")}</Text>) ||
                    (vacancies.length === 0 && <Text>{t("vacancies.none")}</Text>) ||
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 32,
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        {vacancies.map((vacancy) => (
                            <div key={vacancy.ID}>
                                <Title3 as="h4" className={styles.cardTitle}>
                                    {vacancy.title}
                                </Title3>
                                <div style={{ marginBottom: 12 }}>
                                    <Text size={400} as="span" style={{ color: "#444" }}>
                                        <span dangerouslySetInnerHTML={{ __html: vacancy.details }} />
                                    </Text>
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 8 }}>
                                    <div>
                                        <Text size={400} weight="semibold">Location: </Text>
                                        <Text size={400}>{vacancy.location}</Text>
                                    </div>
                                    <div>
                                        <Text size={400} weight="semibold">Salary: </Text>
                                        <Text size={400}>£{vacancy.salary?.toLocaleString()}</Text>
                                    </div>
                                    <div>
                                        <Text size={400} weight="semibold">Target Hire Date: </Text>
                                        <Text size={400}>{vacancy.targetHireDate ? new Date(vacancy.targetHireDate).toLocaleDateString() : "-"}</Text>
                                    </div>
                                </div>
                                {vacancy.idealCandidate && (
                                    <div style={{ marginTop: 8 }}>
                                        <Text size={400} weight="semibold" as="span">Ideal Candidate:</Text>
                                        <Text size={400} as="span" style={{ marginLeft: 0, color: "#444" }}>
                                            <span dangerouslySetInnerHTML={{ __html: vacancy.idealCandidate }} />
                                        </Text>
                                    </div>
                                )}
                                <div style={{ marginTop: 12 }}>
                                    <Text size={200} weight="semibold" as="span">Key Skills:</Text>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {vacancy.skill_dotnet && <li>.NET Core Development</li>}
                                        {vacancy.skill_unit_testing && <li>Software Unit Testing</li>}
                                        {vacancy.skill_sql && <li>SQL Server + Data & BI tools</li>}
                                        {vacancy.skill_documentation && <li>Software Documentation</li>}
                                        {vacancy.skill_m365_consultancy && <li>Microsoft 365 Consultancy</li>}
                                        {vacancy.skill_m365_management && <li>Microsoft 365 Management</li>}
                                        {vacancy.skill_pm && <li>Project Management</li>}
                                        {vacancy.skill_spfx && <li>SharePoint Framework</li>}
                                        {vacancy.skill_az && <li>Microsoft Azure</li>}
                                        {vacancy.skill_sales_marketing && <li>Sales & Marketing</li>}
                                        {vacancy.skill_pbi && <li>PowerBI</li>}
                                        {vacancy.skill_PowerAutomate && <li>Power Automate</li>}
                                        {vacancy.skill_papps && <li>PowerApps</li>}
                                        {vacancy.skill_itsupport && <li>General Support</li>}
                                        {vacancy.skill_other?.trim() && <li>{vacancy.skill_other}</li>}
                                    </ul>
                                </div>
                                <hr style={{ margin: "24px 0" }} />
                            </div>
                        ))}
                    </div>
                }
            </div>
        </section>
    );
}

async function getVacancies (
    setVacancies: (vacancies: IVacancy[]) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const vacancies = await recruitmentService.getVacancies();

        setVacancies(vacancies);
        setStatus(Status.LOAD_SUCCESS);
    } catch (error) {
        setStatus(Status.LOAD_ERROR);
    }
}